import type { StorageType as DomainStorageType } from "encamp-domain-constants";

import * as domainConstants from "encamp-domain-constants";
import { range } from "lodash-es";
import { DateTime } from "luxon";

import { LEPC_IDS } from "./governmentAgencyIds";
import { EPLAN_STATES, StateAbbreviation } from "./states";

export const currentTierIIReportingYear = domainConstants.currentTierIIReportingYear;

/**
 * Minimum year allowed for measurement dates in the UI.
 * This prevents users from accidentally entering dates like 0005-01-20.
 */
export const MIN_MEASUREMENT_YEAR = 2017;

/**
 * The MO portal collects EHS component percentages in five fixed slots, so a
 * mixture with more EHS components than this cannot be filed there.
 */
export const MO_MAX_EHS_COMPONENTS = 5;

/**
 * The MO portal rejects coordinates outside of Missouri client-side. Matching
 * these bounds locally lets us surface the problem before we file.
 *
 * Missouri's extremes are 35.996 N, 40.614 N, -95.775 W and -89.099 W. Each one
 * is rounded outward to the next tenth of a degree - roughly seven miles of
 * slack - because the check is meant to catch coordinates that are missing,
 * zeroed, swapped, or in another state, not to police the state line. Rounding
 * inward would reject real facilities: the northeast corner of the state sits
 * above 40.6.
 */
export const MO_COORDINATE_BOUNDS = {
  minLatitude: 35.9,
  maxLatitude: 40.7,
  minLongitude: -95.8,
  maxLongitude: -89.0,
};

export const STORAGE_TYPE_DESCRIPTIONS = domainConstants.STORAGE_TYPE_DESCRIPTIONS;
export const StorageType = domainConstants.StorageType;
export type StorageType = DomainStorageType;

// Either 365, or 366, for leap year
export const currentTierIIReportingYearDays = DateTime.utc(
  currentTierIIReportingYear,
  12,
  31
).ordinal;

// This constant is used to populate any reporting year filter dropdowns
// Customers can begin working on Initial reports any time that calendar year
const currentCalendarYear = new Date().getFullYear();
export const reportingYearFilterRange = range(currentCalendarYear, 2018);

// Season boundaries are calendar dates, so they are pinned to UTC — built in the
// machine's zone they sit hours off UTC midnight and drift when compared against
// UTC-normalized values.
export const currentTierIISeasonStart = DateTime.utc(currentTierIIReportingYear + 1, 1, 1);

export const currentTierIIDeadline = DateTime.utc(currentTierIIReportingYear + 1, 3, 1);

export const nextTierIIReportingYear = currentTierIIReportingYear + 1;

export const nextTierIISeasonStart = DateTime.utc(nextTierIIReportingYear + 1, 1, 1);

export const nextTierIIDeadline = DateTime.utc(nextTierIIReportingYear + 1, 3, 1);

/**
 * Activity description used when a report has no Tier II credential.
 */
export const CREDENTIAL_REQUIRED_ACTIVITY_DESCRIPTION = "Tier II credential required";

export type DropdownOption = {
  id: string;
  label: string;
};

const mapIntoDropdownOptions: (value: string) => DropdownOption = (value: string) => ({
  id: value,
  label: value,
});

export const AZ_OR_SD_WA_STORAGE_TYPE_DESCRIPTIONS = [
  StorageType.AboveGroundTank as string,
  StorageType.Bag as string,
  StorageType.Battery as string,
  StorageType.BelowGroundTank as string,
  StorageType.Box as string,
  StorageType.Can as string,
  StorageType.Carboy as string,
  StorageType.Cylinder as string,
  StorageType.Dewar as string,
  StorageType.FiberDrum as string,
  StorageType.GlassBottlesOrJugs as string,
  StorageType.Other as string,
  StorageType.OutdoorStoragePile as string,
  StorageType.PlasticBottlesOrJugs as string,
  StorageType.PlasticOrNonMetallicDrum as string,
  StorageType.RailCar as string,
  StorageType.Silo as string,
  StorageType.SteelDrum as string,
  StorageType.TankInsideBuilding as string,
  StorageType.TankWagon as string,
  StorageType.ToteBin as string,
] as const;

export const AZ_STORAGE_TYPE_DESCRIPTIONS = AZ_OR_SD_WA_STORAGE_TYPE_DESCRIPTIONS;

export const CO_STORAGE_TYPE_DESCRIPTIONS = [
  StorageType.AboveGroundTank as string,
  StorageType.Bag as string,
  StorageType.Battery as string,
  StorageType.BelowGroundTank as string,
  StorageType.Box as string,
  StorageType.Can as string,
  StorageType.Carboy as string,
  StorageType.Cylinder as string,
  StorageType.FiberDrum as string,
  StorageType.GlassBottlesOrJugs as string,
  StorageType.Other as string,
  StorageType.OutdoorStoragePile as string,
  StorageType.PlasticBottlesOrJugs as string,
  StorageType.PlasticOrNonMetallicDrum as string,
  StorageType.RailCar as string,
  StorageType.Silo as string,
  StorageType.SteelDrum as string,
  StorageType.TankInsideBuilding as string,
  StorageType.TankWagon as string,
  StorageType.ToteBin as string,
] as const;

export const AR_CT_DE_KY_MN_STORAGE_TYPE_DESCRIPTIONS = [
  StorageType.AboveGroundTank as string,
  StorageType.Bag as string,
  StorageType.BelowGroundTank as string,
  StorageType.Box as string,
  StorageType.Can as string,
  StorageType.Carboy as string,
  StorageType.Cylinder as string,
  StorageType.FiberDrum as string,
  StorageType.GlassBottlesOrJugs as string,
  StorageType.LeadAcidBattery as string,
  StorageType.Other as string,
  StorageType.PlasticBottlesOrJugs as string,
  StorageType.PlasticOrNonMetallicDrum as string,
  StorageType.RailCar as string,
  StorageType.Silo as string,
  StorageType.SteelDrum as string,
  StorageType.TankInsideBuilding as string,
  StorageType.TankWagon as string,
  StorageType.ToteBin as string,
] as const;

export const AR_STORAGE_TYPE_DESCRIPTIONS = AR_CT_DE_KY_MN_STORAGE_TYPE_DESCRIPTIONS;

export const CT_STORAGE_TYPE_DESCRIPTIONS = AR_CT_DE_KY_MN_STORAGE_TYPE_DESCRIPTIONS;

export const DE_STORAGE_TYPE_DESCRIPTIONS = AR_CT_DE_KY_MN_STORAGE_TYPE_DESCRIPTIONS;

export const KY_STORAGE_TYPE_DESCRIPTIONS = AR_CT_DE_KY_MN_STORAGE_TYPE_DESCRIPTIONS;

export const LA_STORAGE_TYPE_TYPED_DESCRIPTIONS = [
  StorageType.AboveGroundTank,
  StorageType.Bag,
  StorageType.BelowGroundTank,
  StorageType.Box,
  StorageType.Can,
  StorageType.Carboy,
  StorageType.Cylinder,
  StorageType.FiberDrum,
  StorageType.GlassBottlesOrJugs,
  StorageType.Other,
  StorageType.PlasticBottlesOrJugs,
  StorageType.PlasticOrNonMetallicDrum,
  StorageType.RailCar,
  StorageType.Silo,
  StorageType.SteelDrum,
  StorageType.TankInsideBuilding,
  StorageType.TankWagon,
  StorageType.ToteBin,
] as const;

export const LA_STORAGE_TYPE_DESCRIPTIONS: string[] = [...LA_STORAGE_TYPE_TYPED_DESCRIPTIONS];

export const MN_STORAGE_TYPE_DESCRIPTIONS = AR_CT_DE_KY_MN_STORAGE_TYPE_DESCRIPTIONS;

export const OR_STORAGE_TYPE_DESCRIPTIONS = AZ_OR_SD_WA_STORAGE_TYPE_DESCRIPTIONS;

export const SD_STORAGE_TYPE_DESCRIPTIONS = AZ_OR_SD_WA_STORAGE_TYPE_DESCRIPTIONS;

export const TX_STORAGE_TYPE_DESCRIPTIONS = [
  StorageType.AboveGroundTank,
  StorageType.Bag,
  StorageType.Battery,
  StorageType.BelowGroundTank,
  StorageType.Box,
  StorageType.Can,
  StorageType.Carboy,
  StorageType.Cylinder,
  StorageType.FiberDrum,
  StorageType.GlassBottlesOrDrums,
  StorageType.Other,
  StorageType.PlasticBottlesOrJugs,
  StorageType.PlasticOrNonMetallicDrum,
  StorageType.RailCar,
  StorageType.Silo,
  StorageType.SteelDrum,
  StorageType.TankInsideBuilding,
  StorageType.TankWagon,
  StorageType.ToteBin,
] as const;

export const EPLAN_STORAGE_TYPE_TYPED_DESCRIPTIONS = [
  StorageType.AboveGroundTank,
  StorageType.Bag,
  StorageType.Battery,
  StorageType.BelowGroundTank,
  StorageType.Box,
  StorageType.Can,
  StorageType.Carboy,
  StorageType.Cylinder,
  StorageType.FiberDrum,
  StorageType.GlassBottlesOrJugs,
  StorageType.OutdoorStoragePile,
  StorageType.PlasticBottlesOrJugs,
  StorageType.PlasticOrNonMetallicDrum,
  StorageType.RailCar,
  StorageType.Silo,
  StorageType.SteelDrum,
  StorageType.TankInsideBuilding,
  StorageType.TankWagon,
  StorageType.ToteBin,
] as const;

export const WA_STORAGE_TYPE_DESCRIPTIONS = AZ_OR_SD_WA_STORAGE_TYPE_DESCRIPTIONS;

const OTHER_STORAGE_TYPE: string = StorageType.Other;

// The FL SERC does not accept "Other"; the rest of the default set is fine.
export const FL_STORAGE_TYPE_DESCRIPTIONS = STORAGE_TYPE_DESCRIPTIONS.filter(
  (description) => description !== OTHER_STORAGE_TYPE
);

const DropdownOptionsByState: Partial<Record<StateAbbreviation, DropdownOption[]>> = {
  AR: AR_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  AZ: AZ_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  // CA has no entry: CERS accepts any type — native container types check their
  // CERS box and the rest are filed under "Other", so the full list is offered.
  CO: CO_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  CT: CT_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  DE: DE_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  FL: FL_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  KY: KY_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  LA: LA_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  MN: MN_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  OR: OR_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  SD: SD_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  TX: TX_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
  WA: WA_STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions),
};

export const disallowCustomStorageTypeStates = ["AZ", "KS", "MO", "SD", "TX", "WA"];

export const getStorageTypeDropdownOptionsForState = (state: StateAbbreviation) => {
  return DropdownOptionsByState[state] || STORAGE_TYPE_DROPDOWN_OPTIONS;
};

export const STORAGE_TYPE_DROPDOWN_OPTIONS = STORAGE_TYPE_DESCRIPTIONS.map(mapIntoDropdownOptions);

export type StorageTypeDescriptions = `${StorageType}`;

export const PRESSURE_DESCRIPTIONS = [
  "Ambient pressure",
  "Greater than ambient pressure",
  "Less than ambient pressure",
] as const;

export type PressureDescriptions = (typeof PRESSURE_DESCRIPTIONS)[number];

export const PRESSURE_DROPDOWN_OPTIONS = PRESSURE_DESCRIPTIONS.map(mapIntoDropdownOptions);

export const TEMPERATURE_DESCRIPTIONS = [
  "Ambient temperature",
  "Cryogenic conditions",
  "Greater than ambient temperature",
  "Less than ambient temperature but not cryogenic",
] as const;

export type TemperatureDescriptions = (typeof TEMPERATURE_DESCRIPTIONS)[number];

export const TEMPERATURE_DROPDOWN_OPTIONS = TEMPERATURE_DESCRIPTIONS.map(mapIntoDropdownOptions);

/*
 * OR specific location enums
 */
export const OR_INSIDE_OUTSIDE_DESCRIPTIONS = ["Inside", "Outside"] as const;

export type ORInsideOutsideDescriptions = (typeof OR_INSIDE_OUTSIDE_DESCRIPTIONS)[number];

export const OR_INSIDE_OUTSIDE_OPTIONS = OR_INSIDE_OUTSIDE_DESCRIPTIONS.map(mapIntoDropdownOptions);

export const OR_STORAGE_QUADRANT_DESCRIPTIONS = [
  "N",
  "S",
  "E",
  "W",
  "NE",
  "SE",
  "NW",
  "SW",
  "C (Center)",
  "V (Various)",
] as const;

export type ORStorageQuadrantDescriptions = (typeof OR_STORAGE_QUADRANT_DESCRIPTIONS)[number];

// Utility type to extract the id part from a string (the part before a space character)
type ExtractID<T extends string> = T extends `${infer ID} ${string}` ? ID : T;

export const OR_STORAGE_QUADRANT_OPTIONS = OR_STORAGE_QUADRANT_DESCRIPTIONS.map((item) => ({
  id: item.split(" ")[0] as ExtractID<typeof item>,
  label: item,
}));

// The various id values that map to OR_STORAGE_QUADRANT_DESCRIPTIONS
export type ORStorageQuadrantOptionID = (typeof OR_STORAGE_QUADRANT_OPTIONS)[number]["id"];

/*
 * LA Specific location options
 */

export const LA_PRESSURE_DESCRIPTIONS = [...PRESSURE_DESCRIPTIONS, "Other"] as const;

const pressureFreeTextIndex = PRESSURE_DESCRIPTIONS.length;
export const LA_PRESSURE_FREE_TEXT_SELECTION = LA_PRESSURE_DESCRIPTIONS[pressureFreeTextIndex];

export type LAPressureDescriptions = (typeof LA_PRESSURE_DESCRIPTIONS)[number];

export const LA_PRESSURE_DROPDOWN_OPTIONS = LA_PRESSURE_DESCRIPTIONS.map(mapIntoDropdownOptions);

export const LA_TEMPERATURE_DESCRIPTIONS = [...TEMPERATURE_DESCRIPTIONS, "Other"] as const;

const temperatureFreeTextIndex = TEMPERATURE_DESCRIPTIONS.length;
export const LA_TEMPERATURE_FREE_TEXT_SELECTION =
  LA_TEMPERATURE_DESCRIPTIONS[temperatureFreeTextIndex];

export type LATEMPERATUREDescriptions = (typeof LA_TEMPERATURE_DESCRIPTIONS)[number];

export const LA_TEMPERATURE_DROPDOWN_OPTIONS =
  LA_TEMPERATURE_DESCRIPTIONS.map(mapIntoDropdownOptions);

/**
 * Jurisdictions that allow multiple simultaneous logins per credential.
 * These don't require credential locking - we can use portal capacity constraints instead.
 */
export const MULTI_LOGIN_PER_CREDENTIAL_JURISDICTIONS: string[] = [
  "AZ",
  "CA",
  "KS",
  "LA",
  "MO",
  "NJ",
  "SD",
  "WA",

  // NYC LEPC portal
  `NY_${LEPC_IDS.NY.NYC}`,

  // E-Plan portal supports multiple simultaneous logins
  ...EPLAN_STATES,
];

// States/territories where compliance validation requires every storage
// location's unit of measure to match the parent facility chemical's unit.
// The storage-location unit field in the UI is derived (locked) in these
// jurisdictions; keep this list in sync only here — the compliance rule and
// the frontend both import it.
//
// `<const>` (not `string[]`) is deliberate: the compliance-engine rule spreads
// this into a `forJurisdictions([...])` call that needs the readonly string
// literal tuple to type-check — don't "simplify" it to `string[]`.
export const STORAGE_UNIT_MUST_MATCH_CHEMICAL_UNIT_JURISDICTIONS = [
  "AS",
  "CA",
  "DC",
  "GU",
  "MP",
  "OR",
  "PR",
  "SD",
] as const;

export function storageUnitMustMatchChemicalUnit(
  facilityState: string | null | undefined
): boolean {
  return (
    !!facilityState &&
    STORAGE_UNIT_MUST_MATCH_CHEMICAL_UNIT_JURISDICTIONS.some((s) => s === facilityState)
  );
}
